<template lang="html">
    <div class="hydrologic-variability-chart-subsection page-break-after">
        <div
            v-if="!pdfLayout"
            class="legend-wrapper is-hidden-touch"
        >
            <div
                class="box candidate-legend"
            >
                <span
                    class="dot candidate-1"
                />Candidate 1
                <br>
                <span
                    class="dot candidate-2"
                />Candidate 2
                <br>
                <span
                    class="dot candidate-3"
                />Candidate 3
            </div>
            <hydrologic-variability-chart-legend
                class="box hydrologic-variability-chart-legend"
                :items="[
                    { label: '90th', y: 0 },
                    { label: '75th', y: 20 },
                    { label: '25th', y: 80 },
                    { label: '10th', y: 100 },
                ]"
            />
            <chart-legend
                :items="getLineData()"
                measurement-unit=" m³/s"
                vertical
            />
        </div>
        <resize-wrapper
            v-slot="{ width, height }"
            throttle
            :interval="125"
            class="hydrologic-variability-chart-wrapper"
        >
            <hydrologic-variability-chart
                class="no-page-break"
                :has-candidates="true"
                :data="reportData.hydrologicVariability"
                :mad-data="getMadChartData()"
                :mean-data="getMeanChartData()"
                :pdf-layout="pdfLayout"
                :width="width"
                :height="height"
            />
        </resize-wrapper>
        <div
            v-if="pdfLayout"
            class="pdf-legend"
        >
            <hr>
            <h5 class="title header is-5">
                Legend
            </h5>
            <div class="legend-container">
                <hydrologic-variability-chart-legend
                    :items="[
                        { label: '90th', y: 0 },
                        { label: '75th', y: 20 },
                        { label: '25th', y: 80 },
                        { label: '10th', y: 100 },
                    ]"
                />
                <chart-legend
                    :items="legendItems"
                    measurement-unit=" m³/s"
                    vertical
                />
            </div>
        </div>
    </div>
</template>

<script>
import ChartLegend from '@foundryspatial/chart-legend';
import ResizeWrapper from '@foundryspatial/resize-wrapper';

import scssVars from '@/assets/scss/_variables.scss';
import HydrologicVariabilityChart from '@/components/charts/HydrologicVariabilityChart';
import HydrologicVariabilityChartLegend from '@/components/charts/HydrologicVariabilityLegend';


export default {
    name: 'HydrologicVariabilitySection',

    components: {
        ChartLegend,
        HydrologicVariabilityChart,
        HydrologicVariabilityChartLegend,
        ResizeWrapper,
    },

    props: {
        reportData: {
            type: Object,
            required: true,
        },
        pdfLayout: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    computed: {
        meanData() {
            return this.reportData.queryMonthlyHydrology.monthlyDischarge;
        },

        legendItems() {
            return [
                {
                    color: scssVars.colorHydroVar1,
                    legendLabel: 'Candidate 1',
                    legendType: 'box',
                    borderColor: '#000',
                },
                {
                    color: scssVars.colorHydroVar2,
                    legendLabel: 'Candidate 2',
                    legendType: 'box',
                    borderColor: '#000',
                },
                {
                    color: scssVars.colorHydroVar3,
                    legendLabel: 'Candidate 3',
                    legendType: 'box',
                    borderColor: '#000',
                },
                ...this.getLineData(),
            ];
        },
    },

    methods: {
        /**
         * Get the mad data for the HydrologicVariabilityChart
         *
         * @returns {Array} - mad data required by HydrologicVariabilityChart
         */
        getMadChartData() {
            // get the appropriate data from the report
            const chartData = this.reportData.queryMonthlyHydrology;

            const { meanAnnualDischarge } = chartData;

            return [
                {
                    data: meanAnnualDischarge * 0.1,
                    color: scssVars.colorMad10,
                    line: true,
                    name: 'mad-10',
                    labelOrder: 7,
                    legendLabel: '10% MAD',
                    legendType: 'dashedLine',
                    legendValue: meanAnnualDischarge * 0.1,
                },
                {
                    data: meanAnnualDischarge * 0.2,
                    color: scssVars.colorMad20,
                    line: true,
                    name: 'mad-20',
                    labelOrder: 6,
                    legendLabel: '20% MAD',
                    legendType: 'dashedLine',
                    legendValue: meanAnnualDischarge * 0.2,
                },
                {
                    data: meanAnnualDischarge,
                    color: scssVars.colorMad,
                    line: true,
                    name: 'mad',
                    labelOrder: 5,
                    legendLabel: 'MAD',
                    legendType: 'dashedLine',
                    legendValue: meanAnnualDischarge,
                },
            ];
        },
        /**
         * Get the mean data for the HydrologicVariabilityChart
         *
         * @returns {Array} - mean data required by HydrologicVariabilityChart
         */
        getMeanChartData() {
            return [
                {
                    data: this.meanData,
                    color: scssVars.colorMean,
                    line: true,
                    solid: true,
                    name: 'mean',
                    labelOrder: 4,
                    legendType: 'solidLine',
                    legendLabel: 'Mean',
                    legendValue: 'Varies',
                },
            ];
        },

        /**
         * Get the data for all chart lines. This is needed for the legend.
         *
         * @returns {Array} - data for all chart lines
         */
        getLineData() {
            return [...this.getMadChartData(), ...this.getMeanChartData()];
        },
    },
};
</script>

<style lang="scss">
.hydrologic-variability-chart-subsection {
    display: flex;
    flex-direction: row;

    @media screen and (max-width: $desktop) {
        flex-direction: column;

        .chart-legend {
            align-self: center;
            max-width: 25em;
        }
    }

    .legend-wrapper {
        margin-right: 1em;
        margin-left: 1px;

        .candidate-legend {
            margin-top: 1em;
            padding: 0.5em;
            background-color: $color-hydrovar-candidate-legend-background;

            span {
                margin: 1em;
            }

            span.dot {
                width: 1em;
                height: 1em;
                border-radius: 0.5em;
                display: inline-block;
                margin-right: 1em;
                vertical-align: middle;
            }

            .candidate-1 {
                background-color: $color-hydrovar-2;
            }

            .candidate-2 {
                background-color: $color-hydrovar-3;
            }

            .candidate-3 {
                background-color: $color-hydrovar-4;
            }
        }

        .hydrologic-variability-chart-legend {
            width: 6.5em;
            margin: 1em 0;
            height: 10em;
            padding: 0.5em;
        }

        .chart-legend {
            margin: 0.25em;
            width: 10.5em;

            .row {
                font-size: 0.65em;
            }
        }
    }

    .hydrologic-variability-chart-wrapper {
        height: 500px;
    }
}

.static-report .hydrologic-variability-chart-subsection {
    flex-direction: column;

    .hydrologic-variability-chart-wrapper {
        height: 350px;
    }

    .pdf-legend {
        .legend-container {
            display: flex;
            flex-direction: row;
            justify-content: center;

            .hydrologic-variability-chart-legend {
                display: flex;
                flex-direction: row;
                margin-right: 60px;

                h3 {
                    font-size: 1em;
                    width: 75px;
                    margin-top: 50px;
                }

                .legend-svg-wrap {
                    width: 75px;
                    height: fit-content;
                    margin-top: 8px;
                }
            }

            .chart-legend {
                width: 240px;

                .legend-item {
                    display: flex;
                    justify-content: flex-end;

                    .row {
                        font-size: 16px;

                        .visual-container {
                            .visual.color-box {
                                width: 16px;
                                height: 16px;
                                border-width: 1px;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
